import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import * as XLSX from "xlsx";

const formatNumberCH = (value, options = {}) => {
    if (value === null || value === undefined || value === "") {
        return "0";
    }
    const numberValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numberValue)) {
        return value?.toString ? value.toString() : "";
    }
    const {
        minimumFractionDigits = 0,
        maximumFractionDigits = Math.max(minimumFractionDigits, 2),
    } = options;
    return numberValue.toLocaleString("de-CH", {
        minimumFractionDigits,
        maximumFractionDigits,
    });
};

const ResultII = () => {
    const [resultData, setResultData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const [availableBuildings, setAvailableBuildings] = useState([]);
    const [availablePeriods, setAvailablePeriods] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    useEffect(() => {
        fetchBuildings();
    }, []);

    const fetchBuildings = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}building/list`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            if (res.data.status === "success") {
                const buildings = res.data.data.buildings || [];
                setAvailableBuildings(
                    buildings.map(b => ({ value: b.id, label: b.building_name }))
                );
            }
        } catch (err) {
            console.error("Error fetching buildings:", err);
        }
    };

    const handleBuildingChange = async (option) => {
        setSelectedBuilding(option);
        setSelectedPeriod(null);
        setAvailablePeriods([]);
        setResultData([]);
        setHasSearched(false);

        if (!option) return;

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}building/${option.value}/periods`,
                {
                    headers: {
                        "api-key": process.env.REACT_APP_API_KEY,
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            if (res.data.status === "success") {
                const periods = res.data.data.periods || [];
                setAvailablePeriods(
                    periods.map(p => ({
                        value: p.id,
                        label: `${new Date(p.start_date).toLocaleDateString()} - ${new Date(p.end_date).toLocaleDateString()}`,
                    }))
                );
            }
        } catch (err) {
            console.error("Error fetching periods:", err);
        }
    };

    const fetchResultII = async () => {
        if (!selectedBuilding) {
            alert("Please select a building.");
            return;
        }
        if (!selectedPeriod) {
            alert("Please select a time period.");
            return;
        }

        try {
            setLoading(true);
            setHasSearched(true);

            const res = await axios.get(`${process.env.REACT_APP_API_URL}result-ii`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                params: {
                    building_id: selectedBuilding.value,
                    period_id: selectedPeriod.value,
                },
            });

            if (res.data.status === "success") {
                setResultData(res.data.data.result_ii || []);
            } else {
                setResultData([]);
                alert("Error loading Result II: " + res.data.message);
            }
        } catch (err) {
            console.error("Error fetching Result II:", err);
            setResultData([]);
            alert("Error loading Result II: " + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setSelectedBuilding(null);
        setSelectedPeriod(null);
        setAvailablePeriods([]);
        setResultData([]);
        setHasSearched(false);
    };

    // Trigger backend repair+recalculate for the selected period and re-fetch Result II.
    // Use this after editing imported data (cost, surface, consumption, ETC/DJF/NJ, object)
    // when the displayed numbers don't reflect the latest changes.
    const handleRefresh = async () => {
        if (!selectedBuilding || !selectedPeriod) {
            alert("Please select a building and period first.");
            return;
        }
        try {
            setRefreshing(true);
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}calculations/repair-recalculate`,
                {
                    building_id: selectedBuilding.value,
                    building_time_period_id: selectedPeriod.value,
                },
                {
                    headers: {
                        "api-key": process.env.REACT_APP_API_KEY,
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            if (res.data && (res.data.status === "success" || res.data.success)) {
                const s = res.data.data || res.data;
                const msg = s?.queued
                    ? "Another refresh is already in progress — it will pick up the latest data."
                    : `Refresh complete: ${s?.keysRecalculated ?? "?"} of ${s?.keysProcessed ?? "?"} keys recalculated.`;
                alert(msg);
                if (hasSearched) await fetchResultII();
            } else {
                alert("Refresh failed: " + (res.data?.message || "unknown error"));
            }
        } catch (err) {
            console.error("Refresh error:", err);
            alert("Refresh failed: " + (err.response?.data?.message || err.message));
        } finally {
            setRefreshing(false);
        }
    };

    const exportToExcel = () => {
        if (resultData.length === 0) {
            alert("No data to export.");
            return;
        }

        const exportData = resultData.map(row => ({
            "Key Name":           row.key_name      || "",
            "Cost Number":        row.cost_number   || "",
            "Object":             row.object        || "",
            "Surface":            row.surface       || "",
            "Consumption":        row.consumption   || "",
            "Result Without VAT": Number.isFinite(Number(row.result_without_vat)) ? Number(row.result_without_vat) : "",
            "Result With VAT":    Number.isFinite(Number(row.result_with_vat))    ? Number(row.result_with_vat)    : "",
            "Destination":        row.destination   || "",
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(wb, ws, "Résultat II");

        const buildingName = selectedBuilding?.label?.replace(/[^a-zA-Z0-9]/g, "_") || "Building";
        const periodLabel  = selectedPeriod?.label?.replace(/[^a-zA-Z0-9]/g, "_")   || "Period";
        XLSX.writeFile(wb, `ResultII_${buildingName}_${periodLabel}.xlsx`);
    };

    const columns = [
        "Key Name",
        "Cost Number",
        "Object",
        "Surface",
        "Consumption",
        "Result Without VAT",
        "Result With VAT",
        "Destination",
    ];

    return (
        <div className="admin-page calculations-page" style={{ overflowX: "hidden", maxWidth: "100vw" }}>
            <div className="admin-page-title d-flex align-items-center justify-content-between">
                <h1 className="page-section-title">Result II</h1>
            </div>

            {/* Filters */}
            <div className="key-card mb-4 p-lg-4 p-3">
                <h5 className="mb-3">Filters</h5>
                <div className="row">
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>Building</label>
                            <div className="custom-select">
                                <Select
                                    options={availableBuildings}
                                    value={selectedBuilding}
                                    onChange={handleBuildingChange}
                                    placeholder="Select building"
                                    isClearable
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>Time Period</label>
                            <div className="custom-select">
                                <Select
                                    options={availablePeriods}
                                    value={selectedPeriod}
                                    onChange={setSelectedPeriod}
                                    placeholder="Select time period"
                                    isDisabled={!selectedBuilding}
                                    isClearable
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <div className="form-group">
                            <label>&nbsp;</label>
                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                onClick={fetchResultII}
                                disabled={!selectedBuilding || !selectedPeriod || refreshing}
                            >
                                Filter
                            </button>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <div className="form-group">
                            <label>&nbsp;</label>
                            <button
                                type="button"
                                className="btn btn-warning w-100"
                                onClick={handleRefresh}
                                disabled={!selectedBuilding || !selectedPeriod || refreshing || loading}
                                title="Rebuild calculations from the latest cost / surface / consumption / ETC / DJF / NJ / object data. Use this when the displayed numbers don't reflect your most recent edits."
                            >
                                {refreshing ? "Refreshing..." : "Refresh Calculations"}
                            </button>
                        </div>
                    </div>
                    <div className="col-md-1">
                        <div className="form-group">
                            <label>&nbsp;</label>
                            <button
                                type="button"
                                className="btn btn-secondary w-100"
                                onClick={handleClearFilters}
                                disabled={refreshing}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                    <div className="col-md-1">
                        <div className="form-group">
                            <label>&nbsp;</label>
                            <button
                                type="button"
                                className="btn btn-success w-100"
                                onClick={exportToExcel}
                                disabled={!hasSearched || resultData.length === 0 || refreshing}
                                title={
                                    !hasSearched
                                        ? "Apply filters first before exporting"
                                        : `Export ${resultData.length} rows`
                                }
                            >
                                Export
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading Result II...</p>
                </div>
            ) : !hasSearched ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <p>Select a building and time period, then click Filter.</p>
                </div>
            ) : resultData.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <p>No Result II data found for the selected filters.</p>
                </div>
            ) : (
                <div
                    className="calculations-table-container"
                    style={{
                        overflowX: "auto",
                        maxWidth: "100%",
                        border: "1px solid var(--border, #ddd)",
                        borderRadius: "8px",
                    }}
                >
                    <table
                        className="table table-bordered table-hover mb-0"
                        style={{ minWidth: "1000px", fontSize: "13px" }}
                    >
                        <thead style={{ background: "var(--primary, #4f46e5)", color: "#fff" }}>
                            <tr>
                                {columns.map(col => (
                                    <th key={col} style={{ whiteSpace: "nowrap", padding: "10px 12px" }}>
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {resultData.map((row, idx) => (
                                <tr key={idx}>
                                    <td>{row.key_name || "-"}</td>
                                    <td>{row.cost_number || "-"}</td>
                                    <td>{row.object || "-"}</td>
                                    <td>{row.surface || "-"}</td>
                                    <td>{row.consumption || "-"}</td>
                                    <td style={{ textAlign: "right" }}>
                                        {formatNumberCH(row.result_without_vat, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 4,
                                        })}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {formatNumberCH(row.result_with_vat, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 4,
                                        })}
                                    </td>
                                    <td>{row.destination || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {hasSearched && resultData.length > 0 && (
                <div className="mt-2 text-muted" style={{ fontSize: "13px" }}>
                    Showing {resultData.length} rows
                </div>
            )}
        </div>
    );
};

export default ResultII;
